import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Badge } from "@/components/primitives";
import { toast } from "sonner";
import { CheckCircle2, LogIn, Wallet, AlertTriangle, Users } from "lucide-react";
import { EarlyCheckinPrompt } from "./EarlyCheckinPrompt";
import { useCheckinGate, GateWrap, CHECKIN_GATE_CODE } from "@/hooks/useCheckinGate";
import api, { formatApiError } from "@/lib/api";
import { docsAPI, fmtINR, docUrl } from "@/lib/documents";
import { notifyDocument } from "@/components/documents/documentToast";
import { PAYMENT_METHODS, REFERENCE_REQUIRED, methodLabel } from "./AdvancePaymentInput";

/** Check-in confirmation: shows advance paid at booking + balance to collect, then converts the reservation to a stay. */
export function CheckInDialog({ reservationId, open, onOpenChange, onDone }) {
  const navigate = useNavigate();
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState("now");
  const [method, setMethod] = useState("cash");
  const [reference, setReference] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null); // EARLY-CHECKIN · check-in result awaiting the early-charge prompt

  useEffect(() => {
    if (!open || !reservationId) return;
    setPreview(null); setMode("now"); setMethod("cash"); setReference("");
    setLoading(true);
    api.get(`/pms/reservations/${reservationId}/check-in-preview`).then((r) => setPreview(r.data))
      .catch((e) => toast.error(formatApiError(e))).finally(() => setLoading(false));
  }, [open, reservationId]);

  const balance = preview?.balance_to_collect ?? 0;
  const collectNow = balance > 0 && mode === "now";
  const gate = useCheckinGate();
  const gated = gate.isBlocked(preview?.arrival_date);

  const confirm = async () => {
    if (gated) { toast.error(gate.reason(preview.arrival_date)); return; }
    if (collectNow && REFERENCE_REQUIRED.has(method) && !reference.trim()) { toast.error(`Reference number is required for ${methodLabel(method)}`); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/pms/reservations/${reservationId}/check-in`, {
        collect_balance: collectNow, method: collectNow ? method : null, reference: collectNow ? reference.trim() || null : null,
      });
      const rcp = data.receipts?.[0];
      toast.success(data.already_checked_in ? "Already checked in" : `Checked in — ${data.stay_code}${rcp ? ` · receipt ${rcp.receipt_number}` : ""}`);
      if (rcp) notifyDocument(`Receipt ${rcp.receipt_number} issued`, { url: docsAPI.receiptPdfUrl(rcp.receipt_id), filename: `Receipt-${rcp.receipt_number}.pdf`, testId: "checkin-receipt-toast" });
      if (data.stay_id) notifyDocument("Registration card ready", { description: data.registration_card?.number, url: docUrl("registration_card", data.stay_id),
        filename: `RegistrationCard-${data.registration_card?.number || data.stay_code}.pdf`, testId: "checkin-regcard-toast", regCard: true });
      onOpenChange(false);
      if (data.stay_id && data.folio_id) setDone(data); else onDone?.(data);
    } catch (e) {
      const body = e?.response?.data || {};
      const d = body.code ? body : body.detail;
      if (e?.response?.status === 409 && d?.code === "ALREADY_CHECKED_IN") {
        toast.warning("Guest is already checked in", { description: d.message, id: "already-checked-in" });
        onOpenChange(false);
        if (d.stay_id) navigate(`/pms/stays/${d.stay_id}`);
      } else if (d?.code === "ROOM_OCCUPIED" || d?.code === CHECKIN_GATE_CODE) {
        toast.error(d.message);
      } else {
        toast.error(d?.decision === "BLOCK" ? `Blocked: ${d.reason}` : formatApiError(e));
      }
    }
    setBusy(false);
  };

  const isGroup = !!preview?.is_multi_room;
  const goGroup = () => { onOpenChange(false); navigate(`/pms/reservations/${reservationId}?tab=group`); };

  return (<>
    <EarlyCheckinPrompt open={!!done} onClose={() => { const d = done; setDone(null); onDone?.(d); }}
      stays={done ? [{ stay_id: done.stay_id, folio_id: done.folio_id, stay_code: done.stay_code, room_number: done.room_number || preview?.room_number, guest_name: preview?.guest_name }] : []} />
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-hocp-bgCard border border-hocp-border md:max-w-[560px]" data-hocp-dialog data-testid="checkin-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><LogIn size={18} strokeWidth={1.5} />Check-in {preview?.code || ""}</DialogTitle>
          <DialogDescription>{preview?.guest_name ? `${preview.guest_name} · ` : ""}{preview?.nights ? `${preview.nights} night${preview.nights === 1 ? "" : "s"}` : "Confirm arrival"}</DialogDescription>
        </DialogHeader>

        {loading && <div className="skeleton-shimmer h-24" />}
        {isGroup && (
          <div className="flex items-start gap-2 rounded-md border border-hocp-mint bg-hocp-softIce px-3 py-2.5 text-[13px]" data-testid="checkin-group-notice">
            <Users size={16} strokeWidth={1.5} className="mt-0.5 shrink-0 text-hocp-deepTeal" />
            <span>This is a group booking with <b>{preview.room_count} rooms</b>. Use Group Check-In to check in every room at once or one room at a time.</span>
          </div>
        )}
        {preview && !isGroup && (
          <div className="space-y-3">
            <div className="rounded-[6px] border border-hocp-border bg-hocp-softIce px-4 py-3 text-[13px] space-y-1" data-testid="checkin-summary">
              <div className="flex justify-between gap-3"><span className="text-hocp-textMuted">Room total (incl. GST):</span><span className="mono" data-testid="checkin-room-total">{preview.rate_configured ? fmtINR(preview.grand_total) : "—"}</span></div>
              {preview.advance_payments.map((a) => (
                <div key={a.payment_id} className="flex justify-between gap-3" data-testid={`checkin-advance-${a.code}`}>
                  <span className="text-hocp-textMuted flex items-center gap-1.5 min-w-0"><Wallet size={13} strokeWidth={1.5} />Advance paid at booking <Badge tone="info">{methodLabel(a.method)}</Badge>{a.reference ? <span className="mono text-[11px] truncate">{a.reference}</span> : null}</span>
                  <span className="mono">− {fmtINR(a.amount)}</span>
                </div>
              ))}
              {preview.rate_configured && (
                <div className="border-t border-hocp-border pt-1 flex justify-between gap-3 font-bold text-hocp-deepTeal">
                  <span>Balance to collect now:</span><span className="mono" data-testid="checkin-balance">{fmtINR(balance)}</span>
                </div>
              )}
            </div>

            {preview.rate_configured && preview.fully_prepaid && (
              <div className="flex items-center gap-2 rounded-md bg-[var(--badge-success-bg)] text-[var(--badge-success-text)] px-3 py-2 text-[13px]" data-testid="checkin-fully-prepaid">
                <CheckCircle2 size={16} strokeWidth={1.75} />Fully paid at booking — the room receipt will be generated on check-in.
              </div>
            )}

            {balance > 0 && (
              <div className="flex items-center gap-2 rounded-md bg-[#FEF3C7] text-[#78530E] px-3 py-2 text-[13px] font-medium" data-testid="checkin-balance-warning">
                <AlertTriangle size={16} strokeWidth={1.75} />Collect {fmtINR(balance)} before handing over the keys.
              </div>
            )}

            {balance > 0 && (
              <RadioGroup value={mode} onValueChange={setMode} className="space-y-2" data-testid="checkin-mode">
                <label className="flex items-start gap-3 rounded-md border border-hocp-border p-3 cursor-pointer has-[[data-state=checked]]:border-hocp-mint">
                  <RadioGroupItem value="now" id="ci-now" data-testid="checkin-mode-now" className="mt-0.5" />
                  <span><span className="block text-[13px] font-semibold">Collect balance now — {fmtINR(balance)}</span><span className="block text-[12px] text-hocp-textMuted">Posts the payment to the folio and prints a room receipt.</span></span>
                </label>
                {mode === "now" && (
                  <div className="grid grid-cols-2 gap-3 pl-8" data-testid="checkin-collect-fields">
                    <div>
                      <Label className="text-[13px] text-hocp-textMuted">Payment Method</Label>
                      <Select value={method} onValueChange={setMethod}>
                        <SelectTrigger data-testid="checkin-method"><SelectValue /></SelectTrigger>
                        <SelectContent>{PAYMENT_METHODS.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-[13px] text-hocp-textMuted">Reference{REFERENCE_REQUIRED.has(method) ? <span className="text-hocp-error"> *</span> : null}</Label>
                      <Input value={reference} onChange={(e) => setReference(e.target.value)} placeholder="TXN / UTR" data-testid="checkin-reference" />
                    </div>
                  </div>
                )}
                <label className="flex items-start gap-3 rounded-md border border-hocp-border p-3 cursor-pointer has-[[data-state=checked]]:border-hocp-mint">
                  <RadioGroupItem value="later" id="ci-later" data-testid="checkin-mode-later" className="mt-0.5" />
                  <span><span className="block text-[13px] font-semibold">Check in, collect at checkout</span><span className="block text-[12px] text-hocp-textMuted">Balance stays on the folio and is settled at departure.</span></span>
                </label>
              </RadioGroup>
            )}
          </div>
        )}

        {gated && (
          <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-[12.5px] text-amber-800" data-testid="checkin-gate-notice">
            <AlertTriangle size={15} strokeWidth={1.5} className="mt-0.5 shrink-0" />
            <span>Check-in is not yet available for this reservation. {gate.reason(preview.arrival_date)}.</span>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="checkin-cancel">Cancel</Button>
          {isGroup ? (
            <Button onClick={goGroup} data-testid="checkin-open-group"><Users size={15} strokeWidth={1.5} className="mr-1.5" />Open Group Check-In</Button>
          ) : (
            <GateWrap blocked={gated} reason={gate.reason(preview?.arrival_date)} testid="checkin-confirm-gate">
              <Button onClick={confirm} disabled={busy || loading || !preview || gated} data-testid="checkin-confirm">
                {busy ? "Checking in…" : collectNow ? `Collect ${fmtINR(balance)} & Check-in` : "Check-in"}
              </Button>
            </GateWrap>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  </>);
}
